import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, CheckCircle2, Copy, Loader2, Send, Users } from 'lucide-react'
import { api } from '@/api/client'
import type { ApiRequestError, CleaningTeam } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ErrorState } from '@/components/ui/error-state'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { cn } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useWaitingAsks } from './crew-asks-query'
import { DEFAULT_PROPERTY_TIME_ZONE, formatCleaningDay, localDate, shortTime } from './presentation'

/**
 * "Message a crew" (CAP-671).
 *
 * Replaces the retired per-job "Cleaning changed" email: instead of Andrei
 * getting one email per confirmed or moved job and copying each one by hand
 * into a text, this generates the SAME batched text on demand -
 * `GET /admin/cleaning/crew-message?team=<id>` already states every job a
 * crew has confirmed but not yet acked, grouped and ordered for texting
 * (CAP-438) - and puts a copy button on it.
 *
 * General on purpose: any crew can be picked, not just the one (Elisa) who
 * gets texted rather than emailed. A crew that acks its own notices, like
 * Alesia's, simply has nothing outstanding most of the time and the dialog
 * says so.
 */

/**
 * The frame every non-draft state sits in: the same rounded surface and
 * minimum height as the draft itself, so switching crews swaps the contents
 * in place instead of collapsing the dialog and growing it back.
 */
function StatePanel({
  icon,
  title,
  children,
  className,
}: {
  icon: ReactNode
  title: string
  children?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex min-h-40 flex-col items-center justify-center rounded-md border border-border bg-surface2 px-6 py-8 text-center',
        className
      )}
    >
      {icon}
      <p className="mt-3 text-sm font-medium text-text">{title}</p>
      {children && <div className="mt-1 max-w-sm text-xs text-textMuted">{children}</div>}
    </div>
  )
}

/**
 * Draft-shaped placeholder: a few ragged lines where the message will be,
 * plus a caption naming what is loading. A bare pulsing box gives no sign
 * anything is happening, and building the draft scans every upcoming job, so
 * it can take a few seconds.
 */
function DraftLoading({ label }: { label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-40 space-y-4 rounded-md border border-border bg-surface2 p-3"
    >
      <div className="space-y-2" aria-hidden="true">
        <LoadingSkeleton className="h-3.5 w-2/5 bg-white/10" />
        <LoadingSkeleton className="h-3.5 w-4/5 bg-white/10" />
        <LoadingSkeleton className="h-3.5 w-3/5 bg-white/10" />
        <LoadingSkeleton className="mt-4 h-3.5 w-1/3 bg-white/10" />
        <LoadingSkeleton className="h-3.5 w-3/4 bg-white/10" />
      </div>
      <p className="flex items-center gap-2 text-xs text-textMuted">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        {label}
      </p>
    </div>
  )
}

interface CrewMessageDialogProps {
  teams: CleaningTeam[]
  /** Preselect a crew, e.g. when opened from a row already scoped to one. */
  initialTeamId?: string | null
  /**
   * The teams request's own error, if it failed. An empty `teams` list is
   * ambiguous on its own - "no active crews" and "couldn't load crews" look
   * identical from here - so the parent's query error is passed through
   * rather than guessed at or replaced with a generic one: `ErrorState`
   * reads the error's own type to decide what recovery it can offer (e.g. a
   * re-authenticate link for an expired Access session), which a synthetic
   * `Error` would silently throw away.
   */
  teamsError?: Error | null
  /**
   * The teams request's first-fetch loading state. Opening this dialog
   * before that request resolves hands it `teams=[]` and `teamsError=null` -
   * indistinguishable, without this, from the genuine "no active crews"
   * case, and an operator on a slow connection can read that false claim
   * and close the dialog before the real list ever arrives.
   */
  teamsLoading?: boolean
  onRetryTeams?: () => void
  onClose: () => void
}

export function CrewMessageDialog({
  teams,
  initialTeamId,
  teamsError,
  teamsLoading,
  onRetryTeams,
  onClose,
}: CrewMessageDialogProps) {
  // Retired crews are still on the record for their payables history, but
  // they are never who Andrei is about to text - the same filter
  // `runCrewChangeNotices` applies before it emails anyone.
  const activeTeams = teams.filter(team => team.active)
  // Only the operator's own pick lives in state. The active selection below is
  // derived fresh every render instead, so a dialog opened before the teams
  // request finishes - `teams=[]`, nothing yet to pick - catches up on its own
  // once the real list arrives, rather than being stuck on whatever a
  // one-time `useState` initializer saw first.
  const [pickedTeamId, setPickedTeamId] = useState<string | null>(null)
  const teamId =
    pickedTeamId && activeTeams.some(team => team.id === pickedTeamId)
      ? pickedTeamId
      : initialTeamId && activeTeams.some(team => team.id === initialTeamId)
        ? initialTeamId
        : (activeTeams[0]?.id ?? null)
  const selectedTeam = activeTeams.find(team => team.id === teamId) ?? null
  const crewName = selectedTeam ? selectedTeam.short_name || selectedTeam.name : 'this crew'
  const [copied, setCopied] = useState(false)
  // Resetting `copied` lived only in `handleTeamChange`, which covers an
  // explicit pick but not `teamId` changing because the DERIVED fallback
  // moved - e.g. a teams refetch retires the crew that was selected. Copying
  // crew A's draft, then having that refetch land before the 2s "Copied"
  // window closes, would show "Copied" on crew B's card while the clipboard
  // still holds A's text. Comparing against the previous render's `teamId`
  // here is the standard "reset state when a derived value changes" pattern
  // - a conditional setState during render, not in an effect - so it catches
  // every path that changes which crew is selected, explicit or derived.
  const [copiedForTeamId, setCopiedForTeamId] = useState(teamId)
  if (teamId !== copiedForTeamId) {
    setCopiedForTeamId(teamId)
    setCopied(false)
  }
  const { toast } = useToast()

  // A plain closure variable would not do here: `handleCopy` below is
  // recreated each render and captures whatever `teamId` that render saw, so
  // a stale closure checking against its OWN `teamId` can never see a crew
  // switch that happened after it started - it is comparing a value to
  // itself. A ref is read fresh at the point the pending copy resolves,
  // after however many renders happened while it was in flight.
  const teamIdRef = useRef(teamId)
  useEffect(() => {
    teamIdRef.current = teamId
  }, [teamId])

  const messageQuery = useQuery({
    queryKey: ['cleaning', 'crew-message', teamId],
    queryFn: () => api.getCleaningCrewMessage(teamId as string),
    enabled: Boolean(teamId),
    // The draft on screen is what gets copied and then recorded as sent, so
    // it must not change underneath the operator: no background refetch on
    // reconnect. A deliberate Retry still refetches.
    refetchOnReconnect: false,
  })

  // Recording the message as sent (CAP-710). The text sent is checked by
  // admin-api against what the jobs produce now; a 409 means something moved
  // between the copy and the click, and the draft is refetched so what is on
  // screen is again what would be recorded.
  const queryClient = useQueryClient()
  const waitingAsks = useWaitingAsks()
  const lastWaiting = (waitingAsks.data?.asks ?? []).find(ask => ask.team_id === teamId) ?? null
  // Said inside the dialog as well as in a toast: the toast lands behind the
  // modal, and this is the one message that says the text must be copied again.
  // Keyed by crew: an error is about the message for ONE crew, and must not
  // follow the operator to another crew's draft.
  const [sentError, setSentError] = useState<{ teamId: string; message: string } | null>(null)
  const markSent = useMutation({
    mutationFn: ({ team, message }: { team: string; message: string }) =>
      api.createCleaningAsk(team, message),
    onSuccess: ({ ask }) => {
      void queryClient.invalidateQueries({ queryKey: ['cleaning', 'asks'] })
      void queryClient.invalidateQueries({ queryKey: ['cleaning', 'jobs'] })
      toast({
        title: 'Recorded as sent',
        description: `Waiting on ${ask.team_name} to reply. Record it from Needs action.`,
      })
      onClose()
    },
    onError: (error: ApiRequestError, { team }) => {
      if (error.status === 409) void messageQuery.refetch()
      const body = error.body as { error?: string } | null | undefined
      setSentError({ teamId: team, message: body?.error ?? error.message })
      toast({
        title: 'Not recorded',
        description: body?.error ?? error.message,
        variant: 'destructive',
      })
    },
  })

  // `copied` resets itself above whenever the derived `teamId` changes, which
  // this triggers - no need to also clear it here.
  const handleTeamChange = (value: string) => {
    setPickedTeamId(value)
  }

  // Disables the picker and the Copy button for the duration of a write
  // (below), rather than letting a second write start while one is still
  // pending: two concurrent `writeText` calls can resolve in either order,
  // and the `teamIdRef` guard above only suppresses the stale write's
  // success UI - it cannot undo a clipboard that a LATER-starting,
  // EARLIER-resolving write has already overwritten. Serializing is the only
  // way the clipboard's actual contents stay in sync with what the dialog
  // claims was copied.
  const [copyPending, setCopyPending] = useState(false)
  // What actually went onto the clipboard, and for which crew. "Mark as sent"
  // records THIS, not whatever the draft says by the time it is clicked: the
  // operator sent what they copied (CAP-710).
  const [copiedText, setCopiedText] = useState<{ teamId: string | null; text: string } | null>(null)

  const handleCopy = async () => {
    const text = messageQuery.data?.message
    // Captured at click time: `writeText` can be left pending by a browser
    // permission prompt, and if the operator switches crews before it
    // settles, this closure's `text` is still the ORIGINAL crew's message.
    // Reporting success against whichever crew is on screen when the promise
    // resolves would label the new crew's card "Copied" while the clipboard
    // actually holds the old one's text.
    const copiedForTeam = teamId
    if (!text) return
    setCopyPending(true)
    try {
      await navigator.clipboard.writeText(text)
      if (teamIdRef.current !== copiedForTeam) return
      setCopiedText({ teamId: copiedForTeam, text })
      setCopied(true)
      toast({ title: 'Copied', description: 'Message copied to clipboard' })
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy the message',
        variant: 'destructive',
      })
    } finally {
      setCopyPending(false)
    }
  }

  return (
    // Not dismissable while "Mark as sent" is in flight: closing would not
    // stop it, and its result would have nowhere to be shown.
    <Dialog open onOpenChange={open => !open && !markSent.isPending && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Message a crew</DialogTitle>
        </DialogHeader>

        <p className="-mt-2 text-sm text-textMuted">
          Every job this crew has confirmed but has not yet acked, including ones that moved - the
          same text you would piece together by hand.
        </p>

        <div className="space-y-1.5">
          <Select
            value={teamId ?? undefined}
            onValueChange={handleTeamChange}
            // Locked while a send is in flight too, so its result cannot land
            // on another crew's draft.
            disabled={copyPending || markSent.isPending}
          >
            <SelectTrigger aria-label="Crew">
              <SelectValue placeholder="Choose a crew" />
            </SelectTrigger>
            <SelectContent>
              {activeTeams.map(team => (
                <SelectItem key={team.id} value={team.id}>
                  {team.short_name || team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {teamsError ? (
          // An empty `teams` list is ambiguous on its own - this is the case
          // where it means "the request failed", not "no active crews". The
          // real error goes through unchanged (not a synthetic one) so
          // `ErrorState` can tell an expired session from an unreachable API
          // from a plain failure, and offer the right recovery for each.
          <div className="min-h-40 rounded-md border border-danger/30 bg-surface2">
            <ErrorState
              compact
              title="Couldn't load crews"
              error={teamsError}
              onRetry={onRetryTeams}
            />
          </div>
        ) : teamsLoading ? (
          // The other way an empty `teams` list is ambiguous: the request is
          // simply still in flight. Checked before `!teamId` below, or this
          // reads as "no active crews" to an operator who opened the dialog
          // on a slow connection, before the real list has arrived.
          <DraftLoading label="Loading crews…" />
        ) : !teamId ? (
          <StatePanel
            icon={<Users className="h-8 w-8 text-textFaint" aria-hidden="true" />}
            title="No active crews to message"
          >
            Add or reactivate a crew in Settings to message it from here.
          </StatePanel>
        ) : messageQuery.isFetching ? (
          // Not just `isLoading`: switching crews can land on a cached
          // response for one already fetched this session, which shows a
          // stale draft - live-and-sendable - while a fresh one loads in the
          // background. A copy button that stays actionable on stale data is
          // exactly the failure mode this dialog exists to avoid.
          <DraftLoading label={`Building ${crewName}'s message…`} />
        ) : messageQuery.error ? (
          <div className="min-h-40 rounded-md border border-danger/30 bg-surface2">
            <ErrorState
              compact
              title={`Couldn't build ${crewName}'s message`}
              error={messageQuery.error}
              onRetry={() => messageQuery.refetch()}
            />
          </div>
        ) : !messageQuery.data?.message && messageQuery.data?.truncated ? (
          // Not the same as a real empty state: a clipped scan can have
          // genuinely found nothing in the properties it reached while still
          // missing outstanding jobs in the ones it did not, so claiming
          // everything is acked here would be stating something the scan
          // never actually confirmed.
          <StatePanel
            className="border-amber/30"
            icon={<AlertTriangle className="h-8 w-8 text-amber" aria-hidden="true" />}
            title="Couldn't check every job"
          >
            <p>
              Scan was clipped before finishing - {crewName} may have outstanding jobs that were not
              counted.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => messageQuery.refetch()}
            >
              Retry
            </Button>
          </StatePanel>
        ) : !messageQuery.data?.message ? (
          <StatePanel
            icon={<CheckCircle2 className="h-8 w-8 text-mint" aria-hidden="true" />}
            title={`Nothing to send ${crewName}`}
          >
            Nothing outstanding - every confirmed job has been acked.
          </StatePanel>
        ) : (
          <div className="space-y-2">
            <pre className="whitespace-pre-wrap rounded-md border border-border bg-surface2 p-3 text-sm text-text">
              {messageQuery.data.message}
            </pre>
            {messageQuery.data.truncated && (
              // Copy is disabled below rather than just noted here: a
              // non-empty draft from a clipped scan can still be missing
              // outstanding jobs beyond where the scan stopped, and the whole
              // point of this dialog is not sending an incomplete schedule.
              <p className="text-xs text-amber">
                Scan was clipped before finishing - this draft may be missing jobs.{' '}
                <button
                  type="button"
                  className="underline underline-offset-2 hover:no-underline"
                  onClick={() => messageQuery.refetch()}
                >
                  Retry
                </button>
              </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-textFaint">
                {messageQuery.data.count} {messageQuery.data.count === 1 ? 'job' : 'jobs'}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopy}
                  disabled={messageQuery.data.truncated || copyPending}
                >
                  {copied ? (
                    <Check className="mr-1.5 h-3.5 w-3.5 text-mint" />
                  ) : (
                    <Copy className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  {copied ? 'Copied' : 'Copy message'}
                </Button>
                {/* Only after it was actually sent: copying alone records
                    nothing, so opening this to look leaves no trace. */}
                <Button
                  size="sm"
                  onClick={() => {
                    const shown = messageQuery.data?.message
                    if (!teamId || !shown) return
                    const copiedHere = copiedText?.teamId === teamId ? copiedText.text : null
                    // Copied, then the draft changed: what was sent is not
                    // what is on screen, and recording either would be a
                    // guess about what the crew has.
                    if (copiedHere !== null && copiedHere !== shown) {
                      setSentError({
                        teamId,
                        message:
                          'The message changed after you copied it. Copy it again and send that one.',
                      })
                      return
                    }
                    setSentError(null)
                    markSent.mutate({ team: teamId, message: copiedHere ?? shown })
                  }}
                  // Not until the crew's waiting messages are known: marking
                  // this sent replaces one, and the note below says so only
                  // if it has loaded.
                  disabled={
                    messageQuery.data.truncated ||
                    markSent.isPending ||
                    !waitingAsks.isSuccess ||
                    // A clipped list may be missing this crew's waiting
                    // message, so "nothing to replace" is not known.
                    waitingAsks.data.truncated
                  }
                >
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                  {markSent.isPending ? 'Recording…' : 'Mark as sent'}
                </Button>
              </div>
            </div>
            {sentError && sentError.teamId === teamId && (
              <p className="text-xs text-danger" role="alert">
                {sentError.message}
              </p>
            )}
            {waitingAsks.data?.truncated && (
              <p className="text-xs text-amber" role="alert">
                Not every crew message could be checked, so an earlier one still waiting on a reply
                may be missed. Marking this as sent is off until they can all be checked.
              </p>
            )}
            {waitingAsks.isError && (
              <p className="text-xs text-danger" role="alert">
                Could not check whether {messageQuery.data.team.name} is still waiting on an earlier
                message.{' '}
                <button
                  type="button"
                  className="underline underline-offset-2 hover:no-underline"
                  onClick={() => void waitingAsks.refetch()}
                >
                  Retry
                </button>
              </p>
            )}
            {lastWaiting && (
              <p className="text-xs text-textMuted">
                Last sent{' '}
                {formatCleaningDay(
                  localDate(lastWaiting.sent_at, DEFAULT_PROPERTY_TIME_ZONE),
                  null
                )}
                {shortTime(lastWaiting.sent_at, DEFAULT_PROPERTY_TIME_ZONE) &&
                  ` at ${shortTime(lastWaiting.sent_at, DEFAULT_PROPERTY_TIME_ZONE)}`}
                , still waiting on a reply. Marking this one as sent replaces it.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
